import { TYPES } from "@/autogen/supported_image_upload_content_types";
import { showToast } from "@/lib/toasts";

// Extensions for the backend's supported types (emails/models/image/routes.py).
// The backend keys off the filename extension when present, else the MIME type,
// so we mirror that to decide before uploading.
const SUPPORTED_EXTENSIONS = new Set([
  ".gif",
  ".jpeg",
  ".jpg",
  ".png",
  ".webp",
]);

const isSupportedImageUpload = (file: File): boolean => {
  const dot = file.name.lastIndexOf(".");
  if (dot !== -1) {
    return SUPPORTED_EXTENSIONS.has(file.name.slice(dot).toLowerCase());
  }
  return (TYPES as readonly string[]).includes(file.type.toLowerCase());
};

// The API only accepts PNG/JPG/GIF/WebP and 422s anything else, including SVG
// and HEIC (emails/models/image/routes.py). A file-input `accept` filter only
// covers the picker dialog — drag-and-drop and paste bypass it — so those paths
// must guard explicitly. Returns true (and surfaces a toast) when the file is
// not an uploadable image type.
export const rejectUnsupportedImageUpload = (file: File): boolean => {
  if (!isSupportedImageUpload(file)) {
    showToast({
      type: "error",
      title: "Unsupported image type",
      message: "Please upload a PNG, JPG, GIF, or WebP image instead.",
    });
    return true;
  }
  return false;
};

// Drop unuploadable images from a dragged/pasted/picked batch, surfacing a toast
// for each rejected file. See rejectUnsupportedImageUpload.
export const filterUploadableImages = (files: File[]): File[] =>
  files.filter((file) => !rejectUnsupportedImageUpload(file));

// The single place an image-upload multipart body is built. `FormData` is an
// untyped side channel that bypasses the OpenAPI-typed client, so centralizing
// the `image` field name and the explicit filename (which the API's extension
// check keys off) keeps every caller from hand-rolling — and mistyping — it.
export const buildImageUploadFormData = (
  file: File,
  metadata?: Record<string, unknown>
): FormData => {
  const formData = new FormData();
  formData.set("image", file, file.name);
  if (metadata) {
    formData.set("metadata", JSON.stringify(metadata));
  }
  return formData;
};

export const convertUrl = (url: string, mode: "full" | "optimized"): string => {
  // Remove the params.
  const pathWithoutQueryParams = url.split("?")[0];
  if (mode === "full") {
    return pathWithoutQueryParams;
  }
  return `${pathWithoutQueryParams}?w=960&fit=max`;
};

// Marker attribute `transformPastedHTML` stamps onto external <img>s so the node
// parser knows to re-host them. Only set on the paste path, never on load, so
// already-stored content is never re-fetched.
export const REHOST_MARKER_ATTR = "data-buttondown-rehost";

// Hosts that already serve Buttondown-owned images (S3 custom domains + the image
// optimizer): attachments.buttondown.com, images.buttondown.com,
// assets.buttondown.email, buttondown.s3.amazonaws.com. Match by suffix so every
// environment's variant is covered.
const BUTTONDOWN_IMAGE_HOST_PATTERNS = [
  /(^|\.)buttondown\.com$/,
  /(^|\.)buttondown\.email$/,
  /(^|\.)buttondown\.s3\.amazonaws\.com$/,
];

// Whether an image src points somewhere we don't control and should therefore be
// re-hosted to our own storage. data:/blob:/relative URLs and our own hosts are
// not "external". Used only on the paste path, so a stray dev-only optimizer host
// slipping through just means a redundant (deduped) re-host, never data loss.
export const isExternalImageUrl = (src: string): boolean => {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) {
    return false;
  }
  let url: URL;
  try {
    url = new URL(src, window.location.href);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }
  if (url.host === window.location.host) {
    return false;
  }
  const host = url.hostname.toLowerCase();
  return !BUTTONDOWN_IMAGE_HOST_PATTERNS.some((pattern) => pattern.test(host));
};

// Decode a `data:` image URL into a File so it can flow through the normal upload
// path (no CORS concern, unlike external http(s) URLs).
export const dataUrlToFile = (dataUrl: string): File | null => {
  const match = /^data:(image\/[a-z0-9.+-]+);base64,(.*)$/i.exec(dataUrl);
  if (!match) {
    return null;
  }
  const [, mimeType, base64] = match;
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const extension = mimeType.split("/")[1]?.split("+")[0] ?? "png";
  return new File([bytes], `pasted-image.${extension}`, { type: mimeType });
};
